import Service from '@ember/service';
import { service } from '@ember/service';
import { computed } from '@ember/object';
import Consts from '../utils/constants';
import PrettyNum from '../utils/pretty-num-wrapper';

/**
 * What is being done in this file:
 *
 * 1. VSupplyPos (+Vs) and VSupplyNeg (-Vs) are being calculated in calculateVSupply.
 *
 * 2. Validation of VSupplyPos and VSupplyNeg.  These two values are validated whenever they are changed.
 *    If any validation fails, an error message is set.
 *
 * 3. Rounding of VSupplyPos and VSupplyNeg.  These two values are rounded.
 *    If the rounding causes an error in validation, the unrounded values will be used.
 *
 */

export default class DriverSupplyService extends Service {
    @service('data') model;
    @service selectionData;
    @service hashService;

    @computed(
        'model.{vSupplyPos,vSupplyNeg,roVoltageDrop,vcm,vp,gain,noiseConfig}',
        'selectionData.{selectedDriver.id,selectedTopology.id}',
    )
    get vSupplyError() {
        let driver = this.selectionData.selectedDriver;

        let data = {
            vSupplyPos: this.model.vSupplyPos,
            vSupplyNeg: this.model.vSupplyNeg,
            vSpanMin: driver.VsMin,
            vSpanMax: driver.VsMax,
            voutHeadroomPos: driver.VoutHdrmPos,
            vinHeadroomPos: driver.VinHdrmPos,
            voutHeadroomNeg: driver.VoutHdrmNeg,
            vinHeadroomNeg: driver.VinHdrmNeg,
            posValues: this._calculateInitialVValuesByType('Pos'),
            negValues: this._calculateInitialVValuesByType('Neg'),
            roVoltageDrop: this.model.roVoltageDrop,
        };

        /**
         * These are the validations:
         *   BodyArgs: these are arguments that can be used in the translation
         *   Invalid: this is the validation calculation.  If this results in false, the validation has failed
         *   Translation: this is the error message that will be output if the validation fails
         */
        let rules = [
            function (data) {
                return {
                    inValid: data.vSupplyPos - data.vSupplyNeg < data.vSpanMin,
                    translation: 'messages.headroom.supplies_too_low',
                };
            },
            function (data) {
                return {
                    inValid: data.vSupplyPos - data.vSupplyNeg > data.vSpanMax,
                    translation: 'messages.headroom.supplies_too_high',
                };
            },
            function (data) {
                return {
                    bodyArgs: { voutMax: PrettyNum.eng3(data.posValues.vout) },
                    inValid: data.posValues.vout + data.voutHeadroomPos + data.roVoltageDrop - data.vSupplyPos > 0,
                    translation: 'messages.headroom.pos_increase_output',
                };
            },
            function (data) {
                return {
                    bodyArgs: { vinMax: PrettyNum.eng3(data.posValues.vn) },
                    inValid: data.posValues.vn + data.vinHeadroomPos - data.vSupplyPos > 0,
                    translation: 'messages.headroom.pos_increase_input',
                };
            },
            function (data) {
                return {
                    bodyArgs: { voutMin: PrettyNum.eng3(data.negValues.vout) },
                    inValid: data.vSupplyNeg - (data.negValues.vout - data.voutHeadroomNeg - data.roVoltageDrop) > 0,
                    translation: 'messages.headroom.neg_decrease_output',
                };
            },
            function (data) {
                return {
                    bodyArgs: { vinMin: PrettyNum.eng3(data.negValues.vn) },
                    inValid: data.vSupplyNeg - (data.negValues.vn - data.vinHeadroomNeg) > 0,
                    translation: 'messages.headroom.neg_decrease_input',
                };
            },
        ];

        /**
         * Here we are taking in the list of rules, the validation data, and the error property to be set
         * This function will process the rules against the provided data and will set the error message if
         * any rule(s) fail.
         */
        return this.model.validateRules(rules, data);
    }

    unRoundedVSupplyPos = null;
    unRoundedVSupplyNeg = null;
    minVSupply = -100;
    maxVSupply = 100;

    // this just convoludes the code - to the EE who has to troubleshoot and support - remove this.
    vSupplyFunctions = {
        Pos: (x, y) => {
            return x + y;
        },
        Neg: (x, y) => {
            return x - y;
        },
    };

    roundVSupply() {
        let vSupplyPos = this.model.vSupplyPos;
        let vSupplyNeg = this.model.vSupplyNeg;
        if (vSupplyPos && vSupplyNeg && !this.vSupplyError) {
            // Round the results and revalidate (if the rounded results are different)
            let roundedVSupplyPos = vSupplyPos.toScale(1, 'ceil'),
                roundedVSupplyNeg = vSupplyNeg.toScale(1, 'floor');
            if (roundedVSupplyPos !== vSupplyPos || roundedVSupplyNeg !== vSupplyNeg) {
                this.set('unRoundedVSupplyPos', vSupplyPos);
                this.set('unRoundedVSupplyNeg', vSupplyNeg);
                this.set('model.vSupplyPos', roundedVSupplyPos);
                this.set('model.vSupplyNeg', roundedVSupplyNeg);
            }
        }
    }

    resetRoundingProperties() {
        if (this.unRoundedVSupplyPos && this.unRoundedVSupplyNeg && this.vSupplyError) {
            // Rounding resulted in an error, return to unrounded
            this.set('model.vSupplyPos', this.unRoundedVSupplyPos);
            this.set('model.vSupplyNeg', this.unRoundedVSupplyNeg);
        }

        this.set('unRoundedVSupplyPos', null);
        this.set('unRoundedVSupplyNeg', null);
    }

    makeRoundingModifications() {
        this.roundVSupply();
        this.resetRoundingProperties();
    }

    calculateVSupply() {
        // start with requirements based on Vref value - what's required to drive fill scale of ADC
        let posInitValues = this._calculateInitialVValuesByType('Pos'),
            negInitValues = this._calculateInitialVValuesByType('Neg');

        // add margin required for input and output headroom, including ro drop and .5V on output for LTspice Universal model
        console.group('%cSupply adjustment for Headroom', 'color:purple');
        let driver = this.selectionData.selectedDriver,
            VinHdrmPos = driver.VinHdrmPos,
            VinHdrmNeg = driver.VinHdrmNeg,
            VoutHdrmPos = driver.VoutHdrmPos,
            VoutHdrmNeg = driver.VoutHdrmNeg;

        let roVoltageDrop = this.model.roVoltageDrop;

        let SupplyPosVin = posInitValues.vn + VinHdrmPos,
            SupplyPosVout = posInitValues.vout + VoutHdrmPos + roVoltageDrop + 0.3,
            SupplyNegVin = negInitValues.vn - VinHdrmNeg,
            SupplyNegVout = negInitValues.vout - (VoutHdrmNeg + roVoltageDrop + 0.3);

        let tempVSupplyPos = Math.max(SupplyPosVin, SupplyPosVout);
        let tempVSupplyNeg = Math.min(SupplyNegVin, SupplyNegVout);

        console.log(
            '%cVin Headroom: Pos = ' +
                this.selectionData.selectedDriver.VinHdrmPos +
                ', Neg = ' +
                this.selectionData.selectedDriver.VinHdrmNeg,
            'color:purple',
        );
        console.log(
            '%cVout Headroom: Pos = ' +
                this.selectionData.selectedDriver.VoutHdrmPos +
                ', Neg = ' +
                this.selectionData.selectedDriver.VoutHdrmNeg,
            'color:purple',
        );
        console.log('%cand additional .5V on output', 'color:purple');

        // add margin required for output load, assuming output load is connected to Vcm
        console.log(
            '%cSupplies adjusted for load: Vs+ =' + tempVSupplyPos + ', Vs- = ' + tempVSupplyNeg,
            'color:purple',
        );

        // adjust if initial supplies don't meet minimum Vspan requirement
        let vSpanMin = this.selectionData.selectedDriver.VsMin,
            vSpanAuto = tempVSupplyPos - tempVSupplyNeg,
            vSupplyPadding = vSpanMin - vSpanAuto;

        if (vSupplyPadding > 0) {
            tempVSupplyPos = tempVSupplyPos + vSupplyPadding / 2;
            tempVSupplyNeg = tempVSupplyNeg - vSupplyPadding / 2;
            console.log('%cAdjustment required to meet minimum vspan of ' + vSpanMin, 'color:purple');
        }

        this.set('model.vSupplyPos', tempVSupplyPos);
        this.set('model.vSupplyNeg', tempVSupplyNeg);
        this.makeRoundingModifications();

        console.log(
            '%cInital Supply settings: Vs+ =' + this.model.vSupplyPos + ', Vs- = ' + this.model.vSupplyNeg,
            'color:purple',
        );
        console.groupEnd();
    }

    _calculateInitialVValuesByType(type) {
        let noiseConfig = this.model.noiseConfig,
            calc = this.vSupplyFunctions[type],
            gain = this.model.gain,
            vcm = this.model.vcm,
            vp = this.model.vp,
            vout = {
                min: vcm - vp,
                max: vcm + vp,
            },
            mathFunc = type === 'Neg' ? 'min' : 'max',
            vSourcePp =
                noiseConfig === Consts.CONFIGURATION.DiffADC_DiffAmp_SESource ? (vp * 4) / gain : (vp * 2) / gain,
            vSourceP = vSourcePp / 2,
            vnOutput = vcm; // default

        if (noiseConfig === Consts.CONFIGURATION.DiffADC_DiffAmp_SESource) {
            vnOutput = vout[mathFunc];
            vnOutput = vnOutput * (1 / (gain + 1));
        } else if (noiseConfig === Consts.CONFIGURATION.DiffADC_DiffAmp_DiffSource) {
            vnOutput = vnOutput * (1 / (gain + 1));
        } else if (
            this.selectionData.selectedDriver.type === Consts.DRIVER_TYPE.AMP &&
            (this.selectionData.selectedTopology.id === Consts.TOPOLOGY.NON_INVERTING ||
                this.selectionData.selectedTopology.id === Consts.TOPOLOGY.FOLLOWER)
        ) {
            vnOutput = calc(vnOutput, vSourceP);
        }

        return {
            vn: vnOutput,
            vout: vout[mathFunc],
        };
    }

    /*
    _calculateVSupplyByType(initValues, type) {
        let mathFunc = this._getMathFuncFromVType(type),
            calc = this.vSupplyFunctions[type],
            vSupplyIn = calc(initValues.vn, this.get(`selectionData.selectedDriver.VinHdrm${type}`)),
            vSupplyOut = calc(initValues.vout, this.get(`selectionData.selectedDriver.VoutHdrm${type}`));

        return Math[mathFunc](vSupplyIn, vSupplyOut);
    },
    */

    // this just makes the code difficult to read, for the EE - remove
    // _getMathFuncFromVType(type) {
    //     return type === 'Neg' ? 'min' : 'max';
    // },
}
